import React from 'react';

/**
 * Internal context value used by `useCookie` to coordinate updates across consumers.
 *
 * @property subscribe - Register a callback that fires when a specific cookie is mutated.
 *   Returns an unsubscribe function.
 * @property notify - Signal all subscribers of a given cookie name to re-read its value.
 */
interface ICookieContextValue {
  subscribe: (cookieName: string, callback: () => void) => () => void;
  notify: (cookieName: string) => void;
}

const CookieContext = React.createContext<ICookieContextValue | undefined>(undefined);

/**
 * Provides a lightweight pub/sub mechanism so that all `useCookie` consumers
 * watching the same cookie name are notified when the cookie value changes.
 *
 * Subscriptions are stored in a ref-based map, so updating a cookie only
 * triggers re-renders in components that subscribe to that specific cookie —
 * the rest of the tree is unaffected.
 *
 */
export const CookieProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const listenersRef = React.useRef(new Map<string, Set<() => void>>());

  const subscribe = React.useCallback((cookieName: string, callback: () => void) => {
    const listeners = listenersRef.current;

    if (!listeners.has(cookieName)) {
      listeners.set(cookieName, new Set());
    }

    listeners.get(cookieName)!.add(callback);

    return () => {
      const set = listeners.get(cookieName);

      if (set) {
        set.delete(callback);

        if (set.size === 0) {
          listeners.delete(cookieName);
        }
      }
    };
  }, []);

  const notify = React.useCallback((cookieName: string) => {
    const set = listenersRef.current.get(cookieName);

    if (set) {
      set.forEach(cb => cb());
    }
  }, []);

  const value = React.useMemo(() => ({ subscribe, notify }), [subscribe, notify]);

  return <CookieContext.Provider value={value}>{children}</CookieContext.Provider>;
};

/**
 * Returns the cookie pub/sub context. Must be called within a `CookieProvider`.
 * @internal Consumed by the `useCookie` hook — not intended for direct use.
 */
export const useCookieContext = () => {
  const context = React.useContext(CookieContext);

  if (!context) {
    throw new Error('useCookieContext must be used within a CookieProvider');
  }

  return context;
};
